import React, { useEffect, useRef, useState } from 'react';
import { useI18n } from '../useI18n.js';
import { serverUrl } from '../socket';
import { MashupWheel } from './MashupWheel';

const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
const clampBpm = (b) => Math.max(40, Math.min(220, Number(b) || 90));

// Encode un AudioBuffer (somme des canaux en mono) en WAV 16-bit.
function encodeWavMono(buffer) {
  const n = buffer.length, rate = buffer.sampleRate, chans = buffer.numberOfChannels;
  const data = new Float32Array(n);
  for (let c = 0; c < chans; c++) { const ch = buffer.getChannelData(c); for (let i = 0; i < n; i++) data[i] += ch[i] / chans; }
  const ab = new ArrayBuffer(44 + n * 2);
  const view = new DataView(ab);
  const wr = (off, s) => { for (let i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i)); };
  wr(0, 'RIFF'); view.setUint32(4, 36 + n * 2, true); wr(8, 'WAVE'); wr(12, 'fmt ');
  view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, 1, true);
  view.setUint32(24, rate, true); view.setUint32(28, rate * 2, true); view.setUint16(32, 2, true); view.setUint16(34, 16, true);
  wr(36, 'data'); view.setUint32(40, n * 2, true);
  let off = 44;
  for (let i = 0; i < n; i++) { const s = Math.max(-1, Math.min(1, data[i])); view.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7fff, true); off += 2; }
  return new Blob([ab], { type: 'audio/wav' });
}

function drawWave(canvas, buffer, color = 'rgba(255,255,255,0.9)') {
  if (!canvas || !buffer) return;
  const ctx = canvas.getContext('2d'); const w = canvas.width, h = canvas.height;
  ctx.clearRect(0, 0, w, h); ctx.fillStyle = color;
  const ch = buffer.getChannelData(0); const step = Math.max(1, Math.floor(ch.length / w));
  for (let x = 0; x < w; x++) {
    let min = 1, max = -1;
    for (let j = 0; j < step; j++) { const v = ch[x * step + j] || 0; if (v < min) min = v; if (v > max) max = v; }
    ctx.fillRect(x, (1 + min) * h / 2, 1, Math.max(1, (max - min) * h / 2));
  }
}

// ---- Reference : le mashup (barre cliquable pour seek) ----
function ReferencePlayer({ src }) {
  const { t } = useI18n();
  const audioRef = useRef(null);
  const barRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);
  const toggle = () => { const a = audioRef.current; if (!a) return; if (a.paused) { a.play().then(() => setPlaying(true)).catch(() => {}); } else { a.pause(); setPlaying(false); } };
  const seek = (e) => { const a = audioRef.current, el = barRef.current; if (!a || !el || !dur) return; const r = el.getBoundingClientRect(); const f = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)); a.currentTime = f * dur; setCur(f * dur); };
  const pct = dur ? (cur / dur) * 100 : 0;
  return (
    <div className="studio-ref">
      <audio ref={audioRef} src={src} preload="metadata" onLoadedMetadata={(e) => setDur(e.target.duration || 0)} onTimeUpdate={(e) => setCur(e.target.currentTime || 0)} onEnded={() => setPlaying(false)} />
      <button type="button" className="btn studio-ref-play" onClick={toggle} title={playing ? 'Pause' : (t('play') || 'Lecture')}>{playing ? '⏸' : '▶'}</button>
      <div ref={barRef} className="studio-ref-bar" onClick={seek}>
        <div className="studio-ref-fill" style={{ width: `${pct}%` }} />
        <div className="studio-ref-head" style={{ left: `${pct}%` }} />
      </div>
      <span className="studio-ref-time">{fmt(cur)} / {fmt(dur || 0)}</span>
    </div>
  );
}

const LANE_H = 56;
const PREROLL = 3;

// Dessine l'enveloppe d'un buffer sur un canvas (barres verticales).
function drawEnvelope(canvas, buffer) {
  if (!canvas || !buffer) return;
  const g = canvas.getContext('2d'); const w = canvas.width, h = canvas.height;
  g.clearRect(0, 0, w, h); g.fillStyle = 'rgba(255,255,255,0.92)';
  const ch = buffer.getChannelData(0); const step = Math.max(1, Math.floor(ch.length / w));
  for (let x = 0; x < w; x++) {
    let peak = 0; for (let j = 0; j < step; j++) { const v = Math.abs(ch[x * step + j] || 0); if (v > peak) peak = v; }
    const bh = Math.max(1, peak * h); g.fillRect(x, (h - bh) / 2, 1, bh);
  }
}
function ClipWave({ buffer }) {
  const ref = useRef(null);
  useEffect(() => { drawEnvelope(ref.current, buffer); }, [buffer]);
  return <canvas ref={ref} width={600} height={LANE_H - 8} className="daw-clip-wave" />;
}

// ---- Phase creation : DAW multipiste (timeline, curseur, record au curseur) ----
function StudioCreate({ info, timeLeft, onSubmit, submitted }) {
  const { t } = useI18n();
  const loopSec = Math.max(4, Math.min(30, info?.loopSec || 15)); // = duree de l'extrait a recreer
  const ctxRef = useRef(null);
  const cancelRef = useRef(false);
  const lanesRef = useRef(null);
  // Extrait a recreer : fenetre de loopSec s prise vers le milieu du mashup.
  const mashupUrl = info?.audioUrl ? `${serverUrl}${info.audioUrl}` : undefined;
  const refAudioRef = useRef(null);
  const refDurRef = useRef(0);
  const [extractStart, setExtractStart] = useState(0);
  const [refPlaying, setRefPlaying] = useState(false);
  const [refCur, setRefCur] = useState(0); // 0..loopSec
  const pickExtract = (dur, avoid) => {
    const lo = 30, hi = dur - 45 - loopSec;
    if (hi <= lo) return Math.max(0, dur / 2 - loopSec / 2);
    let s = lo + Math.random() * (hi - lo);
    for (let i = 0; i < 15 && avoid != null && Math.abs(s - avoid) < 15; i++) s = lo + Math.random() * (hi - lo);
    return s;
  };
  const onRefMeta = (e) => { const d = e.target.duration || 0; refDurRef.current = d; setExtractStart(pickExtract(d, null)); };
  const regenExtract = () => { const a = refAudioRef.current; if (a) a.pause(); setRefPlaying(false); setRefCur(0); setExtractStart((prev) => pickExtract(refDurRef.current, prev)); };
  const toggleRef = () => { const a = refAudioRef.current; if (!a) return; if (a.paused) { a.currentTime = extractStart + refCur; a.play().then(() => setRefPlaying(true)).catch(() => {}); } else { a.pause(); setRefPlaying(false); } };
  const onRefTime = (e) => { const a = e.target; const rel = a.currentTime - extractStart; if (rel >= loopSec) { a.pause(); a.currentTime = extractStart; setRefPlaying(false); setRefCur(0); } else setRefCur(Math.max(0, rel)); };
  const seekExtract = (clientX, el) => { const r = el.getBoundingClientRect(); const f = Math.max(0, Math.min(1, (clientX - r.left) / r.width)); const rc = f * loopSec; setRefCur(rc); const a = refAudioRef.current; if (a) a.currentTime = extractStart + rc; };
  const [tracks, setTracks] = useState([]);          // { id, buffer, offset, volume }
  const [cursor, setCursor] = useState(0);            // position de lecture (s)
  const [playing, setPlaying] = useState(false);
  const [rec, setRec] = useState('idle');             // idle | countin | recording
  const [countdown, setCountdown] = useState(0);
  const [bpm, setBpm] = useState(90);
  const [metro, setMetro] = useState(false);
  const [monitor, setMonitor] = useState('mashup'); // ce qu'on entend en enregistrant : mashup | tracks | none
  const [err, setErr] = useState('');
  const monAudioRef = useRef(null); // lecteur mashup dedie au monitoring (pre-roll + enregistrement)
  const armed = useRef(false);
  // refs de lecture / enregistrement
  const cursorRef = useRef(0);
  const srcsRef = useRef([]);
  const anchorRef = useRef(null);       // { ctxTime, cursor }
  const rafRef = useRef(0);
  const playStartRef = useRef(0);       // curseur au dernier lancement de lecture
  const recStartRef = useRef(0);        // position de debut d'enregistrement
  const recCanvasRef = useRef(null);
  const recStopRef = useRef(null);       // resolve() pour arreter l'enregistrement tot
  const recStopTimerRef = useRef(null);
  const dragRef = useRef(null);

  useEffect(() => { cursorRef.current = cursor; }, [cursor]);
  const ensureCtx = () => { if (!ctxRef.current) ctxRef.current = new (window.AudioContext || window.webkitAudioContext)(); return ctxRef.current; };
  const resumeCtx = async () => { const c = ensureCtx(); if (c.state === 'suspended') { try { await c.resume(); } catch {} } return c; };
  useEffect(() => () => { cancelRef.current = true; cancelAnimationFrame(rafRef.current); try { ctxRef.current && ctxRef.current.close(); } catch {} }, []);

  useEffect(() => {
    if (timeLeft > 1) armed.current = true;
    if (armed.current && !submitted && timeLeft <= 1) doSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft]);

  // --- Metronome persistant (toggle), grille stable ---
  const metroTimerRef = useRef(null);
  const metroStartRef = useRef(null);
  const nextNoteRef = useRef(0);
  const clickAt = (ctx, when) => {
    const osc = ctx.createOscillator(); const g = ctx.createGain();
    osc.frequency.value = 1600; g.gain.setValueAtTime(0.0001, when);
    g.gain.exponentialRampToValueAtTime(0.35, when + 0.001); g.gain.exponentialRampToValueAtTime(0.0001, when + 0.05);
    osc.connect(g).connect(ctx.destination); osc.start(when); osc.stop(when + 0.06);
  };
  useEffect(() => {
    if (!metro) { if (metroTimerRef.current) { clearInterval(metroTimerRef.current); metroTimerRef.current = null; } return; }
    let stopped = false;
    resumeCtx().then((ctx) => {
      if (stopped) return;
      const beat = 60 / clampBpm(bpm);
      if (metroStartRef.current == null) metroStartRef.current = ctx.currentTime + 0.1;
      const elapsed = ctx.currentTime - metroStartRef.current;
      nextNoteRef.current = metroStartRef.current + Math.max(0, Math.ceil(elapsed / beat)) * beat;
      metroTimerRef.current = setInterval(() => {
        const b = 60 / clampBpm(bpm);
        while (nextNoteRef.current < ctx.currentTime + 0.12) { clickAt(ctx, nextNoteRef.current); nextNoteRef.current += b; }
      }, 25);
    });
    return () => { stopped = true; if (metroTimerRef.current) { clearInterval(metroTimerRef.current); metroTimerRef.current = null; } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metro, bpm]);

  // Programme les pistes pour que la position "fromSec" soit entendue a "when0".
  const scheduleFrom = (ctx, when0, fromSec, list) => {
    const out = [];
    for (const tr of list) {
      const dur = tr.buffer.duration;
      const clipEnd = tr.offset + dur;
      if (clipEnd <= fromSec) continue;         // deja passe
      const bufOffset = Math.max(0, fromSec - tr.offset);
      if (bufOffset >= dur) continue;
      const when = when0 + Math.max(0, tr.offset - fromSec);
      const src = ctx.createBufferSource(); src.buffer = tr.buffer;
      const g = ctx.createGain(); g.gain.value = tr.volume ?? 1;
      src.connect(g).connect(ctx.destination);
      src.start(when, bufOffset);
      out.push(src);
    }
    return out;
  };
  const stopSources = () => { for (const s of srcsRef.current) { try { s.stop(); } catch {} } srcsRef.current = []; };

  const tickPlayhead = () => {
    const ctx = ctxRef.current; const a = anchorRef.current;
    if (!ctx || !a) return;
    const c = a.cursor + (ctx.currentTime - a.ctxTime);
    if (c >= loopSec) { stopSources(); setPlaying(false); anchorRef.current = null; setCursor(loopSec); cursorRef.current = loopSec; return; }
    cursorRef.current = c; setCursor(c);
    rafRef.current = requestAnimationFrame(tickPlayhead);
  };

  const play = async () => {
    const ctx = await resumeCtx();
    stopSources();
    const from = cursorRef.current >= loopSec ? 0 : cursorRef.current;
    playStartRef.current = from;
    const when0 = ctx.currentTime + 0.06;
    srcsRef.current = scheduleFrom(ctx, when0, from, tracks);
    anchorRef.current = { ctxTime: when0, cursor: from };
    setPlaying(true);
    cancelAnimationFrame(rafRef.current); rafRef.current = requestAnimationFrame(tickPlayhead);
  };
  const pause = () => {
    const ctx = ctxRef.current; const a = anchorRef.current;
    if (ctx && a) { const c = Math.min(loopSec, a.cursor + (ctx.currentTime - a.ctxTime)); setCursor(c); cursorRef.current = c; }
    stopSources(); setPlaying(false); anchorRef.current = null; cancelAnimationFrame(rafRef.current);
  };
  const stop = () => { stopSources(); setPlaying(false); anchorRef.current = null; cancelAnimationFrame(rafRef.current); setCursor(0); cursorRef.current = 0; };

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const startRecord = async () => {
    if (rec !== 'idle' || submitted) return;
    setErr('');
    let stream;
    try { stream = await navigator.mediaDevices.getUserMedia({ audio: true }); }
    catch { setErr(t('studioMicDenied') || 'Micro refusé ou indisponible.'); return; }
    const ctx = await resumeCtx();
    cancelRef.current = false;
    // On coupe la lecture et on repart de l'endroit ou la lecture avait demarre.
    const recStart = playing ? playStartRef.current : cursorRef.current;
    stopSources(); setPlaying(false); anchorRef.current = null; cancelAnimationFrame(rafRef.current);
    recStartRef.current = recStart;
    setCursor(recStart); cursorRef.current = recStart;

    // Pre-roll : on entend TOUJOURS les 3 s de mashup qui precedent le point de
    // record (le contexte qui sera greffe au reveal). La timeline [0..loopSec]
    // correspond a la fenetre mashup [extractStart .. extractStart+loopSec].
    setRec('countin');
    const mon = monAudioRef.current;
    const recPoint = extractStart + recStart;           // position dans le mashup
    const preFrom = Math.max(0, recPoint - PREROLL);
    const preLen = Math.min(PREROLL, recPoint - preFrom); // <= 3s
    if (mon && mon.src) { try { mon.currentTime = preFrom; mon.volume = 1; mon.play().catch(() => {}); } catch {} }
    const preStart = performance.now();
    const cursorFrom = Math.max(0, recStart - preLen);
    const preTick = () => {
      const el = (performance.now() - preStart) / 1000;
      const c = Math.min(recStart, cursorFrom + el); cursorRef.current = c; setCursor(c);
      if (!cancelRef.current && performance.now() - preStart < preLen * 1000) rafRef.current = requestAnimationFrame(preTick);
    };
    cancelAnimationFrame(rafRef.current); rafRef.current = requestAnimationFrame(preTick);
    for (let c = 3; c >= 1; c--) { setCountdown(c); await sleep((preLen || PREROLL) * 1000 / 3); if (cancelRef.current) { stream.getTracks().forEach((x) => x.stop()); if (mon) mon.pause(); stop(); setRec('idle'); setCountdown(0); return; } }
    setCountdown(0);
    cancelAnimationFrame(rafRef.current);

    // Monitoring pendant l'enregistrement (radio : mashup / pistes / rien).
    setRec('recording');
    if (monitor === 'mashup') { if (mon) { try { mon.currentTime = recPoint; } catch {} } }
    else { if (mon) mon.pause(); }
    if (monitor === 'tracks') srcsRef.current = scheduleFrom(ctx, ctx.currentTime + 0.05, recStart, tracks);
    const chunks = [];
    let mr;
    try { mr = new MediaRecorder(stream); } catch { setErr('MediaRecorder indisponible.'); stream.getTracks().forEach((x) => x.stop()); stop(); setRec('idle'); return; }
    mr.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
    mr.onstop = async () => {
      stream.getTracks().forEach((x) => x.stop());
      try {
        const decoded = await ctx.decodeAudioData(await new Blob(chunks).arrayBuffer());
        setTracks((l) => [...l, { id: `t${l.length}_${Math.floor(cursorRef.current * 1000)}`, buffer: decoded, offset: recStartRef.current, volume: 1 }]);
      } catch { setErr(t('studioDecodeErr') || 'Échec du décodage de la prise.'); }
      setRec('idle');
    };
    // Analyser -> spectre progressif (de gauche a droite, en suivant le curseur).
    const micSrc = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser(); analyser.fftSize = 1024;
    micSrc.connect(analyser);
    const buf = new Uint8Array(analyser.fftSize);
    const rc = recCanvasRef.current; if (rc) rc.getContext('2d').clearRect(0, 0, rc.width, rc.height);
    mr.start();
    const recLen = Math.max(0.5, loopSec - recStart);
    const startT = performance.now();
    const drawProg = () => {
      const el = recCanvasRef.current;
      const elapsed = (performance.now() - startT) / 1000;
      const c = Math.min(loopSec, recStart + elapsed); cursorRef.current = c; setCursor(c);
      if (el) {
        analyser.getByteTimeDomainData(buf);
        let peak = 0; for (let i = 0; i < buf.length; i++) { const v = Math.abs(buf[i] / 128 - 1); if (v > peak) peak = v; }
        const w = el.width, h = el.height; const x = Math.min(w - 1, Math.floor((elapsed / recLen) * w));
        const g = el.getContext('2d'); g.fillStyle = '#fde047'; const bh = Math.max(1, peak * h); g.fillRect(x, (h - bh) / 2, 2, bh);
      }
      if (!cancelRef.current && performance.now() - startT < recLen * 1000) rafRef.current = requestAnimationFrame(drawProg);
    };
    rafRef.current = requestAnimationFrame(drawProg);
    // Attend la fin de la timeline OU un arret manuel (piste plus courte).
    await new Promise((resolve) => {
      recStopRef.current = resolve;
      const to = setTimeout(() => { if (recStopRef.current) { recStopRef.current = null; resolve(); } }, recLen * 1000);
      recStopTimerRef.current = to;
    });
    cancelAnimationFrame(rafRef.current);
    if (monAudioRef.current) { try { monAudioRef.current.pause(); } catch {} }
    stopSources();
    if (mr.state !== 'inactive') mr.stop();
  };
  // Arret manuel de l'enregistrement (avant la fin de la timeline).
  const stopRecord = () => {
    cancelRef.current = true;
    if (recStopTimerRef.current) { clearTimeout(recStopTimerRef.current); recStopTimerRef.current = null; }
    if (recStopRef.current) { const r = recStopRef.current; recStopRef.current = null; r(); }
  };

  const removeTrack = (id) => setTracks((l) => l.filter((x) => x.id !== id));
  const setVol = (id, v) => setTracks((l) => l.map((x) => x.id === id ? { ...x, volume: v } : x));

  // Clic sur la timeline -> place le curseur.
  const seekAt = (clientX) => {
    const el = lanesRef.current; if (!el) return;
    const r = el.getBoundingClientRect();
    const f = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
    const c = f * loopSec; setCursor(c); cursorRef.current = c;
    if (playing) play(); // relance depuis la nouvelle position
  };

  // Drag d'un clip : horizontal = decalage (offset), vertical = reordonnancement.
  const onClipDown = (e, tr, index) => {
    e.stopPropagation();
    const el = lanesRef.current; const r = el.getBoundingClientRect();
    const pxPerSec = r.width / loopSec;
    dragRef.current = { id: tr.id, startX: e.clientX, startY: e.clientY, origOffset: tr.offset, origIndex: index, pxPerSec };
    const move = (ev) => {
      const d = dragRef.current; if (!d) return;
      const dxSec = (ev.clientX - d.startX) / d.pxPerSec;
      const dIdx = Math.round((ev.clientY - d.startY) / LANE_H);
      setTracks((l) => {
        const cur = l.slice();
        const from = cur.findIndex((x) => x.id === d.id); if (from < 0) return l;
        const dur = cur[from].buffer.duration;
        const newOffset = Math.max(-dur + 0.4, Math.min(loopSec - 0.4, d.origOffset + dxSec));
        cur[from] = { ...cur[from], offset: newOffset };
        const target = Math.max(0, Math.min(cur.length - 1, d.origIndex + dIdx));
        if (target !== from) { const [it] = cur.splice(from, 1); cur.splice(target, 0, it); }
        return cur;
      });
    };
    const up = () => { dragRef.current = null; window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };

  const doSubmit = async () => {
    if (submitted) return;
    const ctx = ensureCtx();
    const rate = ctx.sampleRate;
    const off = new OfflineAudioContext(1, Math.max(1, Math.floor(loopSec * rate)), rate);
    for (const tr of tracks) {
      const src = off.createBufferSource(); src.buffer = tr.buffer;
      const g = off.createGain(); g.gain.value = tr.volume ?? 1;
      src.connect(g).connect(off.destination);
      if (tr.offset >= 0) src.start(tr.offset, 0);
      else src.start(0, -tr.offset);
    }
    let blob;
    try { blob = encodeWavMono(await off.startRendering()); }
    catch { blob = new Blob([new ArrayBuffer(44)], { type: 'audio/wav' }); }
    onSubmit(await blob.arrayBuffer(), 'audio/wav', extractStart);
  };

  const recording = rec === 'recording';
  const busy = rec !== 'idle';
  const pct = (s) => `${Math.max(0, Math.min(100, (s / loopSec) * 100))}%`;
  const recLeft = pct(recStartRef.current);
  const recWidth = `${Math.max(0, Math.min(100, ((cursor - recStartRef.current) / loopSec) * 100))}%`;

  return (
    <div className="studio-room">
      <div className="create-head">
        <div className="create-prompt">
          <span className="cp-label">{t('studioRecreate') || 'Recrée ce mashup'}</span>
          <span className="cp-text">{info?.mashupName} <span style={{ opacity: 0.7 }}>- {info?.mashupAuthor}</span></span>
        </div>
        {timeLeft > 0 && <span className="chrono-timer">{fmt(timeLeft)}</span>}
      </div>

      {/* Lecteur mashup dedie au monitoring (pre-roll + enregistrement) */}
      <audio ref={monAudioRef} src={mashupUrl} preload="auto" style={{ display: 'none' }} />

      {/* Extrait a recreer (fenetre isolee du mashup) */}
      <div className="studio-ref">
        <audio ref={refAudioRef} src={mashupUrl} preload="metadata" onLoadedMetadata={onRefMeta} onTimeUpdate={onRefTime} onEnded={() => setRefPlaying(false)} />
        <button type="button" className="btn studio-ref-play" onClick={toggleRef}>{refPlaying ? '⏸' : '▶'}</button>
        <div className="studio-ref-bar" onClick={(e) => seekExtract(e.clientX, e.currentTarget)}>
          <div className="studio-ref-fill" style={{ width: `${(refCur / loopSec) * 100}%` }} />
          <div className="studio-ref-head" style={{ left: `${(refCur / loopSec) * 100}%` }} />
        </div>
        <span className="studio-ref-time">{fmt(refCur)} / {fmt(loopSec)}</span>
      </div>
      <button type="button" className="studio-bad-extract" onClick={regenExtract}>{t('studioBadExtract') || 'Mon extrait ne convient pas'}</button>

      <div className="studio-controls">
        <button type="button" className={`btn studio-metro-btn${metro ? ' on' : ''}`} onClick={() => setMetro((m) => !m)}>🥁 {t('studioMetro') || 'Métronome'}</button>
        <label className="studio-bpm">BPM <input type="number" min={40} max={220} value={bpm} onChange={(e) => setBpm(parseInt(e.target.value, 10) || 90)} /></label>
        <div className="studio-monitor">
          <span className="sm-label">{t('studioMonitor') || 'Écouter'} :</span>
          {[['mashup', t('studioMonMashup') || 'Mashup'], ['tracks', t('studioMonTracks') || 'Pistes'], ['none', t('studioMonNone') || 'Rien']].map(([v, lbl]) => (
            <label key={v} className={`sm-opt${monitor === v ? ' on' : ''}`}>
              <input type="radio" name="studio-monitor" checked={monitor === v} onChange={() => setMonitor(v)} /> {lbl}
            </label>
          ))}
        </div>
      </div>

      {err && <div className="studio-err">{err}</div>}

      {/* DAW : timeline (pistes) a gauche + controles a droite */}
      <div className="daw">
        <div className="daw-lanes" ref={lanesRef} onPointerDown={(e) => { if (!busy) seekAt(e.clientX); }} style={{ minHeight: LANE_H * Math.max(1, tracks.length + (recording ? 1 : 0)) }}>
          {tracks.map((tr, i) => (
            <div key={tr.id} className="daw-lane" style={{ top: i * LANE_H, height: LANE_H }}>
              <div className="daw-clip" style={{ left: `${(tr.offset / loopSec) * 100}%`, width: pct(tr.buffer.duration) }} onPointerDown={(e) => onClipDown(e, tr, i)}>
                <ClipWave buffer={tr.buffer} />
              </div>
            </div>
          ))}
          {recording && (
            <div className="daw-lane" style={{ top: tracks.length * LANE_H, height: LANE_H }}>
              <div className="daw-clip rec" style={{ left: recLeft, width: recWidth }}>
                <canvas ref={recCanvasRef} width={600} height={LANE_H - 8} className="daw-clip-wave" />
              </div>
            </div>
          )}
          {/* Curseur de lecture (cliquable/draggable via la timeline) */}
          <div className="daw-playhead" style={{ left: pct(cursor) }}
            onPointerDown={(e) => { e.stopPropagation(); const move = (ev) => seekAt(ev.clientX); const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); }; window.addEventListener('pointermove', move); window.addEventListener('pointerup', up); }} />
          {rec === 'countin' && <div className="daw-count">{countdown}</div>}
          {!tracks.length && !recording && <div className="daw-empty">{t('studioEmptyHint') || 'Place le curseur puis ⏺ pour enregistrer'}</div>}
        </div>
        <div className="daw-side">
          {tracks.map((tr) => (
            <div key={tr.id} className="daw-ctrl" style={{ height: LANE_H }}>
              <input className="daw-vol" type="range" min={0} max={1} step={0.05} value={tr.volume} onChange={(e) => setVol(tr.id, parseFloat(e.target.value))} title="Volume" />
              <button type="button" className="btn daw-del" onClick={() => removeTrack(tr.id)} title="Supprimer">🗑</button>
            </div>
          ))}
          {recording && <div className="daw-ctrl" style={{ height: LANE_H }} />}
        </div>
      </div>

      {/* Bouton d'enregistrement d'une piste, sous la derniere piste */}
      {!submitted && (
        <button type="button" className={`studio-rec-track${recording ? ' recording' : ''}`}
          onClick={() => (recording ? stopRecord() : startRecord())} disabled={rec === 'countin'}>
          {recording ? `⏹ ${t('studioStopRec') || 'Arrêter l’enregistrement'}`
            : rec === 'countin' ? `${countdown}…`
              : `⏺ ${t('studioRecTrack') || 'Enregistrer une piste'}`}
        </button>
      )}

      {/* Transport centre sous la timeline */}
      <div className="daw-transport">
        <button type="button" className="btn daw-tp" onClick={stop} disabled={busy} title="Stop">⏹</button>
        <button type="button" className="btn daw-tp play" onClick={() => (playing ? pause() : play())} disabled={busy || !tracks.length} title={playing ? 'Pause' : 'Lecture'}>{playing ? '⏸' : '▶'}</button>
      </div>

      {submitted
        ? <div className="mashup-status">{t('studioSubmitted') || 'Création envoyée, en attente des autres...'}</div>
        : <button type="button" className="btn studio-submit" onClick={doSubmit} disabled={busy}>✅ {t('studioSubmit') || 'Valider ma création'}</button>}
    </div>
  );
}

// ---- Phase notation : ecoute chaque creation + note 1..5 ----
function StudioRate({ items, timeLeft, onRate, roomCode }) {
  const { t } = useI18n();
  const [rated, setRated] = useState({});
  const rate = (id, s) => { setRated((r) => ({ ...r, [id]: s })); onRate(id, s); };
  return (
    <div className="studio-room">
      <div className="create-head"><div className="create-prompt"><span className="cp-label">{t('studioRateTitle') || 'Note les créations'}</span></div>
        {timeLeft > 0 && <span className="chrono-timer">{timeLeft}s</span>}</div>
      <div className="studio-rate-list">
        {(items || []).map((it, i) => (
          <div key={it.id} className="studio-rate-item">
            <div className="sr-head">🎵 {t('studioCreation') || 'Création'} #{i + 1} <span style={{ opacity: 0.7 }}>({it.mashupName})</span></div>
            <audio controls preload="none" src={`${serverUrl}/api/rooms/${roomCode || ''}/creation/${it.id}`} style={{ width: '100%' }} />
            <div className="sr-stars">
              {[1, 2, 3, 4, 5].map((s) => (
                <button key={s} type="button" className={`sr-star${(rated[it.id] || 0) >= s ? ' on' : ''}`} onClick={() => rate(it.id, s)}>★</button>
              ))}
            </div>
          </div>
        ))}
        {(!items || items.length === 0) && <div className="mashup-status">…</div>}
      </div>
    </div>
  );
}

// ---- Phase reveal : classement + auto-lecture (7 s de mashup + creation) ----
function StudioReveal({ ranking }) {
  const { t } = useI18n();
  const list = ranking || [];
  const audioRef = useRef(null);
  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState('preroll'); // preroll | creation
  useEffect(() => {
    const item = list[idx];
    const a = audioRef.current;
    if (!item || !a) return;
    let cancelled = false;
    const next = () => { if (!cancelled) setTimeout(() => { setPhase('preroll'); setIdx((i) => i + 1); }, 700); };
    const playCreation = () => {
      if (cancelled) return;
      setPhase('creation');
      a.onloadedmetadata = null; a.ontimeupdate = null;
      a.src = `${serverUrl}${item.audioUrl}`;
      a.onended = next;
      a.oncanplay = () => { a.oncanplay = null; a.play().catch(() => {}); };
      a.load();
    };
    // Pre-contexte : 7 s de mashup juste avant l'extrait recree.
    if (item.trackUrl && item.extractStart >= 1) {
      setPhase('preroll');
      a.onended = null;
      a.src = `${serverUrl}${item.trackUrl}`;
      const from = Math.max(0, item.extractStart - 7);
      a.onloadedmetadata = () => { a.currentTime = from; a.play().catch(() => {}); };
      a.ontimeupdate = () => { if (a.currentTime >= item.extractStart) { a.ontimeupdate = null; playCreation(); } };
      a.load();
    } else {
      playCreation();
    }
    return () => { cancelled = true; try { a.pause(); } catch {} a.onended = null; a.ontimeupdate = null; a.onloadedmetadata = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx]);

  return (
    <div className="studio-room">
      <div className="create-head"><div className="create-prompt"><span className="cp-label">{t('studioResults') || 'Résultats'}</span></div></div>
      <audio ref={audioRef} preload="auto" style={{ display: 'none' }} />
      <div className="studio-rate-list">
        {list.map((r, i) => (
          <div key={r.id || r.pseudo} className={`studio-rate-item${i === idx ? ' playing' : ''}`}>
            <div className="sr-head">{i === idx ? (phase === 'preroll' ? '🎧 ' : '▶ ') : ''}#{r.rank} · {r.pseudo} <span style={{ opacity: 0.7 }}>({r.mashupName})</span> · ⭐ {r.total}</div>
            {r.audioUrl && <audio controls preload="none" src={`${serverUrl}${r.audioUrl}`} style={{ width: '100%' }} />}
          </div>
        ))}
      </div>
    </div>
  );
}

export function StudioRoom({ phase, assign, info, items, ranking, timeLeft, onSubmitCreation, onRate, roomCode }) {
  const [submitted, setSubmitted] = useState(false);
  useEffect(() => { setSubmitted(false); }, [info?.mashupName]);
  if (phase === 'wheel') return <MashupWheel assign={assign} />;
  if (phase === 'rating') return <StudioRate items={items} timeLeft={timeLeft} onRate={onRate} roomCode={roomCode} />;
  if (phase === 'reveal') return <StudioReveal ranking={ranking} />;
  if (phase !== 'creating') return <div className="studio-room"><div className="mashup-status">…</div></div>;
  const submit = (arr, ct, extractStart) => { setSubmitted(true); onSubmitCreation(arr, ct, extractStart); };
  return <StudioCreate info={info} timeLeft={timeLeft} onSubmit={submit} submitted={submitted} />;
}
